import React from "react";
import { useTranslation } from "react-i18next";
import {
  Container,
  Typography,
  Box,
  Stack,
  Chip,
  Paper,
  Divider,
  Button,
  CircularProgress,
} from "@mui/material";
import { motion } from "framer-motion";
import BusinessIcon from "@mui/icons-material/Business";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CodeIcon from "@mui/icons-material/Code";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { useExperiences } from "../../hooks/useExperiences";

const ExperiencePage = () => {
  const { t, i18n } = useTranslation();
  const { data: experiences = [], isLoading } = useExperiences({
    language: i18n.language,
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString(i18n.language, {
      month: "short",
      year: "numeric",
    });
  };

  const cvUrls = {
    fr: "https://ejawolqfzczjrbtexuad.supabase.co/storage/v1/object/public/documents/cv-fr-portfolio.pdf",
    en: "https://ejawolqfzczjrbtexuad.supabase.co/storage/v1/object/public/documents/cv-en-portfolio.pdf",
  };

  const currentCvUrl = cvUrls[i18n.language] || cvUrls.en;

  return (
    <Box
      component="main"
      sx={{ bgcolor: "#0B0F14", minHeight: "100vh", pb: 20 }}
    >
      {/* Header Section */}
      <Box
        sx={{
          py: 15,
          bgcolor: "#111827",
          borderBottom: "1px solid rgba(255,255,255,0.05)",
        }}
      >
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="overline"
              align="center"
              sx={{
                color: "#D4AF37",
                fontWeight: 700,
                letterSpacing: 3,
                display: "block",
                mb: 2,
              }}
            >
              {t("experiences.header.overline")}
            </Typography>
            <Typography
              variant="h1"
              align="center"
              sx={{
                color: "white",
                mb: 3,
                fontFamily: "Philosopher",
                fontSize: { xs: "2.5rem", md: "3.5rem" },
              }}
            >
              {t("experiences.header.titlePrefix")}{" "}
              <span style={{ color: "#0FA3A3" }}>
                {t("experiences.header.titleHighlight")}
              </span>{" "}
              {t("experiences.header.titleSuffix")}
            </Typography>
            <Typography
              variant="h6"
              align="center"
              sx={{
                color: "#9CA3AF",
                fontWeight: 300,
                lineHeight: 1.6,
                maxWidth: "700px",
                mx: "auto",
              }}
            >
              {t("experiences.header.subtitle")}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ mt: 10 }}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
            <CircularProgress sx={{ color: "#D4AF37" }} />
          </Box>
        ) : (
          <Stack spacing={6}>
            {experiences.map((exp, index) => (
              <motion.div
                key={exp.id}
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
              >
                <Paper
                  sx={{
                    p: { xs: 4, md: 6 },
                    bgcolor: "#1F2937",
                    borderRadius: "32px",
                    border: "1px solid",
                    borderColor: exp.is_current
                      ? "#0FA3A3"
                      : "rgba(255,255,255,0.05)",
                    position: "relative",
                    overflow: "hidden",
                    transition: "0.3s",
                    "&:hover": {
                      borderColor: "#D4AF37",
                      boxShadow: "0 20px 40px rgba(0,0,0,0.4)",
                    },
                  }}
                >
                  {exp.is_current && (
                    <Chip
                      label={t("experiences.current")}
                      sx={{
                        position: "absolute",
                        top: 24,
                        right: 24,
                        bgcolor: "#0FA3A3",
                        color: "white",
                        fontWeight: 800,
                        px: 1,
                      }}
                    />
                  )}

                  <Stack spacing={3}>
                    <Box>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ mb: 1 }}
                      >
                        <CalendarTodayIcon
                          sx={{ color: "#D4AF37", fontSize: "1rem" }}
                        />
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: "#D4AF37",
                            fontWeight: 700,
                            letterSpacing: 1,
                          }}
                        >
                          {formatDate(exp.start_date)} -{" "}
                          {exp.is_current
                            ? t("experiences.present")
                            : formatDate(exp.end_date)}
                        </Typography>
                      </Stack>
                      <Typography
                        variant="h3"
                        sx={{
                          color: "white",
                          fontFamily: "Philosopher",
                          fontWeight: 700,
                          mb: 1,
                        }}
                      >
                        {exp.role || exp.title}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <BusinessIcon
                          sx={{ color: "#0FA3A3", fontSize: "1.2rem" }}
                        />
                        <Typography
                          variant="h6"
                          sx={{ color: "#0FA3A3", fontWeight: 600 }}
                        >
                          {exp.company}
                        </Typography>
                      </Stack>
                    </Box>

                    <Divider sx={{ borderColor: "rgba(255,255,255,0.05)" }} />

                    <Box>
                      <Typography
                        variant="body1"
                        sx={{
                          color: "#9CA3AF",
                          lineHeight: 1.8,
                          whiteSpace: "pre-line",
                          fontSize: "1.05rem",
                        }}
                      >
                        {exp.description}
                      </Typography>
                    </Box>

                    {/* Technologies & Skills Section */}
                    {(exp.technologies?.length > 0 ||
                      exp.skills?.length > 0) && (
                      <Box sx={{ pt: 2 }}>
                        {/* Technologies */}
                        {exp.technologies?.length > 0 && (
                          <Box sx={{ mb: 3 }}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              sx={{ mb: 2 }}
                            >
                              <CodeIcon
                                sx={{ color: "#D4AF37", fontSize: "1.1rem" }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "white",
                                  fontWeight: 700,
                                  textTransform: "uppercase",
                                  letterSpacing: 1,
                                }}
                              >
                                {t("experiences.sections.technologies")}
                              </Typography>
                            </Stack>
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1.5,
                              }}
                            >
                              {exp.technologies.map((tech) => (
                                <Chip
                                  key={tech}
                                  label={tech}
                                  sx={{
                                    bgcolor: "rgba(15, 163, 163, 0.05)",
                                    color: "#0FA3A3",
                                    border: "1px solid rgba(15, 163, 163, 0.2)",
                                    fontWeight: 600,
                                    "&:hover": {
                                      bgcolor: "rgba(15, 163, 163, 0.1)",
                                    },
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        )}

                        {/* Skills */}
                        {exp.skills?.length > 0 && (
                          <Box>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              sx={{ mb: 2 }}
                            >
                              <AutoFixHighIcon
                                sx={{ color: "#0FA3A3", fontSize: "1.1rem" }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "white",
                                  fontWeight: 700,
                                  textTransform: "uppercase",
                                  letterSpacing: 1,
                                }}
                              >
                                {t("experiences.sections.skills")}
                              </Typography>
                            </Stack>
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1.5,
                              }}
                            >
                              {exp.skills.map((skill) => (
                                <Chip
                                  key={skill}
                                  label={skill}
                                  sx={{
                                    bgcolor: "rgba(212, 175, 55, 0.05)",
                                    color: "#D4AF37",
                                    border: "1px solid rgba(212, 175, 55, 0.2)",
                                    fontWeight: 600,
                                    "&:hover": {
                                      bgcolor: "rgba(212, 175, 55, 0.1)",
                                    },
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Stack>
                </Paper>
              </motion.div>
            ))}
          </Stack>
        )}

        {/* Professional Summary Footer */}
        <Box
          sx={{
            mt: 15,
            p: 8,
            bgcolor: "rgba(212, 175, 55, 0.03)",
            borderRadius: "40px",
            textAlign: "center",
            border: "1px solid rgba(212, 175, 55, 0.1)",
          }}
        >
          <Typography
            variant="h4"
            sx={{ color: "white", mb: 3, fontFamily: "Philosopher" }}
          >
            {t("experiences.footer.titlePrefix")}{" "}
            <span style={{ color: "#D4AF37" }}>
              {t("experiences.footer.titleHighlight")}
            </span>
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "#9CA3AF", maxWidth: "700px", mx: "auto", mb: 5 }}
          >
            {t("experiences.footer.subtitle")}
          </Typography>
          <Button
            variant="outlined"
            size="large"
            component="a"
            href={currentCvUrl}
            target="_blank"
            sx={{
              color: "#D4AF37",
              borderColor: "#D4AF37",
              px: 5,
              py: 1.5,
              borderRadius: "12px",
              fontWeight: 800,
              "&:hover": {
                borderColor: "#B8962E",
                bgcolor: "rgba(212, 175, 55, 0.05)",
              },
            }}
          >
            {t("experiences.footer.downloadCv")}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default ExperiencePage;
